/**
 * Blueprint Generation Service
 *
 * Generates visual layout blueprints using the visual guide endpoint (Gemini).
 * Called by Orchestrator before writeSlide for layout-changing edits.
 */

import { LiveTrackingStorage } from "../../services/tracking/liveTrackingStorage";
import { SlideCapture } from "../../services/capture/slideCapture";
import { BACKEND_BASE_URL } from "../../services/communication/backendClient";
import { getOrCreatedocumentId } from "../../services/documentIdManager";
import { getContextResolver } from "../core/contextResolver";

export interface BlueprintGenerationParams {
    slideIndex: number;
    intent: string;
    constraints?: string;
    slideImageId?: string;
    contextImages?: Array<{ imageId: string; purpose: string }>;
}

export interface BlueprintGenerationResult {
    success: boolean;
    blueprintId?: string;
    originalSlideImageId?: string;
    error?: string;
}

interface ResolvedContextImage {
    imageBase64: string;
    purpose: string;
}

export class BlueprintService {
    private storage: LiveTrackingStorage;
    private slideCapture: SlideCapture;

    constructor() {
        this.storage = new LiveTrackingStorage();
        this.slideCapture = new SlideCapture(this.storage);
    }

    public async generateBlueprint(params: BlueprintGenerationParams): Promise<BlueprintGenerationResult> {
        console.log(`[BlueprintService] Generating layout blueprint for slide ${params.slideIndex}`);

        try {
            const resolver = getContextResolver();

            // Reuse cached slide image from readSlide if available
            let slideImageBase64: string | undefined;
            if (params.slideImageId) {
                const cached = resolver.getImage(params.slideImageId);
                if (cached) {
                    slideImageBase64 = cached.base64;
                    console.log(`[BlueprintService] Reusing cached slide image: ${params.slideImageId}`);
                } else {
                    console.warn(`[BlueprintService] slideImageId ${params.slideImageId} not found in cache, falling back to capture`);
                }
            }

            if (!slideImageBase64) {
                const documentId = await getOrCreatedocumentId();
                await this.storage.initialize();
                await this.slideCapture.initialize();
                const captureResult = await this.captureSlide(documentId, params.slideIndex);
                if (!captureResult?.snapshot?.imageBase64) {
                    return {
                        success: false,
                        error: `Failed to capture slide ${params.slideIndex}`,
                    };
                }
                slideImageBase64 = captureResult.snapshot.imageBase64;
            }

            const blueprintResult = await this.generateVisualGuide(
                params,
                slideImageBase64
            );

            if (!blueprintResult) {
                return {
                    success: false,
                    error: 'Blueprint generation returned no image',
                };
            }

            const blueprintId = resolver.store('blueprint', {
                slideIndex: params.slideIndex,
                intent: params.intent,
                generatedAt: Date.now(),
                mode: 'layout',
            });

            await resolver.storeImageAsync(
                blueprintId,
                blueprintResult.image,
                blueprintResult.mimeType,
                {
                    source: 'blueprintService',
                    slideIndex: params.slideIndex,
                    purpose: `Layout blueprint for slide ${params.slideIndex}`,
                }
            );

            let originalSlideImageId: string;
            if (params.slideImageId && resolver.getImage(params.slideImageId)) {
                originalSlideImageId = params.slideImageId;
            } else {
                originalSlideImageId = resolver.store('asset', {
                    slideIndex: params.slideIndex,
                    generatedAt: Date.now(),
                    type: 'current_slide_image',
                });
                await resolver.storeImageAsync(
                    originalSlideImageId,
                    slideImageBase64,
                    'image/png',
                    {
                        source: 'blueprintService',
                        slideIndex: params.slideIndex,
                        purpose: `Current slide image for slide ${params.slideIndex}`,
                    }
                );
            }

            console.log(`[BlueprintService] Blueprint stored as ${blueprintId}`);

            return {
                success: true,
                blueprintId,
                originalSlideImageId,
            };
        } catch (error) {
            console.error('[BlueprintService] Error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }

    private resolveContextImages(params: BlueprintGenerationParams): ResolvedContextImage[] {
        if (!params.contextImages?.length) return [];

        const resolver = getContextResolver();
        const resolved: ResolvedContextImage[] = [];

        for (const ctx of params.contextImages) {
            const cachedImage = resolver.getImage(ctx.imageId);
            if (cachedImage) {
                resolved.push({
                    imageBase64: cachedImage.base64,
                    purpose: ctx.purpose,
                });
            } else {
                console.warn(`[BlueprintService] Could not resolve context image: ${ctx.imageId}`);
            }
        }

        return resolved;
    }

    private async generateVisualGuide(
        params: BlueprintGenerationParams,
        currentSlideImage: string
    ): Promise<{ image: string; mimeType: string } | undefined> {
        try {
            const resolvedContextImages = this.resolveContextImages(params);
            const prompt = this.buildBlueprintPrompt(params, resolvedContextImages);

            const orderedImages = [
                currentSlideImage,
                ...resolvedContextImages.map(c => c.imageBase64).slice(0, 4),
            ];

            const MAX_RETRIES = 2;
            const RETRY_DELAY_MS = 2000;

            for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                const result = await this.callVisualGuideEndpoint(prompt, orderedImages, params.slideIndex);

                if (result.retryable && attempt < MAX_RETRIES) {
                    console.warn(`[BlueprintService] Retrying blueprint generation (attempt ${attempt + 2}/${MAX_RETRIES + 1}) after ${RETRY_DELAY_MS}ms`);
                    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
                    continue;
                }

                if (result.image) {
                    return result.image;
                }

                return undefined;
            }

            return undefined;
        } catch (error) {
            console.warn('[BlueprintService] Blueprint generation error:', error);
            return undefined;
        }
    }

    private async callVisualGuideEndpoint(
        prompt: string,
        orderedImages: string[],
        slideIndex: number
    ): Promise<{ image?: { image: string; mimeType: string }; retryable: boolean }> {
        const BLUEPRINT_TIMEOUT_MS = 60000;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), BLUEPRINT_TIMEOUT_MS);

        let response: Response;
        try {
            response = await fetch(`${BACKEND_BASE_URL}/slides/generate-visual-guide`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    instructions: prompt,
                    reference_images: orderedImages,
                    master_data: {
                        target_slide_index: slideIndex,
                    },
                }),
                signal: controller.signal,
            });
        } catch (error) {
            console.warn('[BlueprintService] Fetch error:', error);
            return { retryable: true };
        } finally {
            clearTimeout(timeoutId);
        }

        if (!response.ok) {
            const errorText = await response.text();
            const retryable = response.status === 503 || response.status === 429;
            console.warn(`[BlueprintService] Blueprint generation failed: ${response.status} - ${errorText}`);
            return { retryable };
        }

        const result = await response.json();

        if (result.success && result.image_base64) {
            const mimeType = result.mime_type || 'image/jpeg';
            return { image: { image: result.image_base64, mimeType }, retryable: false };
        }

        console.warn(`[BlueprintService] Blueprint returned no image: ${result.error || 'unknown'}`);
        return { retryable: false };
    }

    private buildBlueprintPrompt(
        params: BlueprintGenerationParams,
        resolvedContextImages: ResolvedContextImage[]
    ): string {
        const referenceSlides = resolvedContextImages.filter(c =>
            c.purpose.toLowerCase().includes('reference') ||
            c.purpose.toLowerCase().includes('match') ||
            c.purpose.toLowerCase().includes('alignment')
        );

        const assetImages = resolvedContextImages.filter(c =>
            c.purpose.toLowerCase().includes('logo') ||
            c.purpose.toLowerCase().includes('add') ||
            c.purpose.toLowerCase().includes('insert')
        );

        let prompt = `<INTRO>
You are a spatial layout planner. Look at the current slide image and the requested change below.
Your job is to perform SPATIAL REASONING to determine the optimal layout AFTER the change, then generate a LAYOUT DIAGRAM showing the result.

**SPATIAL REASONING STEPS** (do this BEFORE drawing):
1. INVENTORY: Count and identify all sections, groups, and items on the current slide
2. CHANGE IMPACT: Determine what the requested change adds, removes, or moves
3. SPACE ALLOCATION: When items are added to or removed from a section, resize it relative to its neighboring sections:
   - A section that GAINS items must GROW — make it wider or taller than its neighbors that didn't change
   - A section that LOSES items must SHRINK — make it narrower or shorter than its neighbors
   - Compare the changed section to adjacent sections: if it now has more items, it must be visibly larger; if fewer, visibly smaller
   - Adjacent sections in the same row/column adjust to compensate (total slide area is fixed)
4. ITEM SIZING: Items within a section should be sized to fill the section evenly:
   - Fewer items in a section = each item is LARGER
   - More items in a section = each item is SMALLER
   - Sections with the same number of items should have the same item sizes
5. ALIGNMENT: Preserve alignment patterns from the input (shared edges, centered headers, equal spacing)

**OUTPUT FORMAT**: A clean diagram with colored bounding boxes representing each section/shape.
- Use distinct colors for each section (blue, green, red, yellow, etc.)
- Label each box with its section name or shape purpose
- Show the EXACT proportions and positions of the target layout
- Keep the same slide dimensions and aspect ratio as the input
- Do NOT reproduce the actual slide content (photos, detailed text, etc.)
- Just show colored rectangles with labels showing the target layout
</INTRO>

<USER_INTENT>
${params.intent}
</USER_INTENT>`;

        if (params.constraints) {
            prompt += `

<CONSTRAINTS>
${params.constraints}

Rules:
- Sections/elements listed here must remain in the SAME position/size as the input
- Only reposition/resize what is needed for the requested change
</CONSTRAINTS>`;
        }

        prompt += `

<RULES>
1. Output a LAYOUT DIAGRAM with colored bounding boxes - NOT a full slide reproduction
2. Each section/group gets a distinctly colored rectangle with a text label
3. Individual items within a section should be shown as smaller boxes inside the section box
4. PRESERVE unchanged areas exactly as they appear in the input image
5. REBALANCE changed areas proportionally — sections with more items get more space, sections with fewer items get less space
6. SIZE items within sections proportionally — a section with N items should divide its space evenly among them
7. Show the slide boundary as a thin border
8. Make proportions accurate - this will be used to extract exact coordinates
9. When a section gains or loses items, it MUST resize relative to neighboring sections — never keep adjacent sections the same size if the change made their item counts differ.
</RULES>`;

        if (resolvedContextImages.length > 0) {
            prompt += `

<INPUT_IMAGES>
- **Image 1 (TARGET)**: Current state of slide ${params.slideIndex} - show the AFTER layout`;

            referenceSlides.forEach((r, i) => {
                prompt += `
- **Image ${i + 2} (REFERENCE)**: ${r.purpose}`;
            });

            assetImages.forEach((a, i) => {
                prompt += `
- **Image ${i + 2 + referenceSlides.length} (ASSET)**: ${a.purpose}`;
            });

            prompt += `
</INPUT_IMAGES>`;
        }

        prompt += `

<EXECUTE>
Generate a layout diagram showing slide ${params.slideIndex} after the change.
Show colored bounding boxes with labels for each section and shape group.
Match the slide's aspect ratio and proportions exactly.
Remember: when items are added or removed, the affected section must resize relative to its neighbors.
</EXECUTE>`;

        return prompt;
    }

    private async captureSlide(documentId: string, slideIndex: number): Promise<any> {
        return PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load('items');
            await context.sync();

            const idx = slideIndex - 1;
            if (idx < 0 || idx >= slides.items.length) {
                console.warn(`[BlueprintService] Slide index ${slideIndex} out of range`);
                return undefined;
            }

            const targetSlide = slides.items[idx];
            return this.slideCapture.captureAndSync(context, targetSlide, idx, documentId);
        });
    }
}
